import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { cn } from "../../lib/utils";

export type SortDirection = "asc" | "desc" | null;

export type SortConfig = {
  key: string;
  direction: SortDirection;
};

export type TableColumn<T> = {
  key: string;
  header: React.ReactNode;
  render: (row: T, rowIndex: number) => React.ReactNode;
  cellClassName?: string;
  headerClassName?: string;
  sortable?: boolean;
  sortFn?: (a: T, b: T) => number;
  getSortValue?: (row: T) => unknown;
};

type DataTableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  enableSorting?: boolean;
  initialSort?: SortConfig;
  onSortChange?: (sortConfig: SortConfig | null) => void;
  onRowClick?: (row: T, rowIndex: number) => void;
  isRowClickable?: (row: T, rowIndex: number) => boolean;
  emptyState?: React.ReactNode;
  containerClassName?: string;
  tableClassName?: string;
  rowClassName?: string | ((row: T, rowIndex: number) => string);
  globalCellClassName?: string;
  scrollMaxHeight?: string;
  getRowKey?: (row: T, rowIndex: number) => string | number;
};

const defaultSort = (a: unknown, b: unknown): number => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  const aStr = toComparableString(a);
  const bStr = toComparableString(b);
  if (aStr < bStr) return -1;
  if (aStr > bStr) return 1;
  return 0;
};

function toComparableString(value: unknown): string {
  if (typeof value === "string") return value.toLowerCase();
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value).toLowerCase();
  }

  return JSON.stringify(value).toLowerCase();
}

const SortableHeader = memo(
  ({
    column,
    sortConfig,
    onSort,
    children,
  }: {
    column: TableColumn<unknown>;
    sortConfig: SortConfig | null;
    onSort: (columnKey: string) => void;
    children: React.ReactNode;
  }) => {
    const isActive = sortConfig?.key === column.key;
    const direction = isActive ? sortConfig.direction : null;

    const handleClick = useCallback(() => {
      onSort(column.key);
    }, [onSort, column.key]);

    if (!column.sortable) {
      return <>{children}</>;
    }

    const isRight = column.headerClassName?.includes("text-right");
    const isCenter = column.headerClassName?.includes("text-center");

    return (
      <button
        type="button"
        className={cn(
          "flex items-center cursor-pointer select-none hover:text-surface-300 transition-colors duration-150",
          isRight && "justify-end",
          isCenter && "justify-center",
        )}
        onClick={handleClick}
      >
        <span className="mr-1.5 shrink-0">
          {!direction && <ChevronsUpDown className="size-3.5 text-surface-600" />}
          {direction === "asc" && <ChevronUp className="size-3.5 text-brand-400" />}
          {direction === "desc" && <ChevronDown className="size-3.5 text-brand-400" />}
        </span>
        <span>{children}</span>
      </button>
    );
  },
);

SortableHeader.displayName = "SortableHeader";

function TableRow<T>({
  row,
  rowIndex,
  columns,
  rowClassName,
  globalCellClassName,
  onRowClick,
  isClickable,
}: {
  row: T;
  rowIndex: number;
  columns: TableColumn<T>[];
  rowClassName?: string | ((row: T, rowIndex: number) => string);
  globalCellClassName?: string;
  onRowClick?: (row: T, rowIndex: number) => void;
  isClickable: boolean;
}) {
  const customRowClassName =
    typeof rowClassName === "function" ? rowClassName(row, rowIndex) : rowClassName;

  const handleClick = useCallback(() => {
    if (isClickable && onRowClick) onRowClick(row, rowIndex);
  }, [isClickable, onRowClick, row, rowIndex]);

  return (
    <tr
      className={cn(
        "border-b border-separator last:border-b-0 transition-colors hover:bg-surface-800/60",
        isClickable && "cursor-pointer",
        customRowClassName,
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      {columns.map((col) => (
        <td
          key={col.key}
          className={cn("px-3 py-1.5 text-sm", globalCellClassName, col.cellClassName)}
        >
          {col.render(row, rowIndex)}
        </td>
      ))}
    </tr>
  );
}

export function DataTable<T>({
  columns,
  data,
  enableSorting = false,
  initialSort,
  onSortChange,
  onRowClick,
  isRowClickable,
  emptyState,
  containerClassName,
  tableClassName,
  rowClassName,
  globalCellClassName,
  scrollMaxHeight = "max-h-[600px]",
  getRowKey,
}: DataTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(initialSort ?? null);

  const handleSort = useCallback(
    (columnKey: string) => {
      if (!enableSorting) return;
      setSortConfig((current) => {
        let next: SortConfig | null;
        if (!current || current.key !== columnKey) {
          next = { key: columnKey, direction: "asc" };
        } else if (current.direction === "asc") {
          next = { key: columnKey, direction: "desc" };
        } else {
          next = null;
        }
        onSortChange?.(next);
        return next;
      });
    },
    [enableSorting, onSortChange],
  );

  const sortedData = useMemo(() => {
    if (!enableSorting || !sortConfig?.direction) return data;
    const column = columns.find((col) => col.key === sortConfig.key);
    if (!column?.sortable) return data;

    return [...data].sort((a, b) => {
      let result: number;
      if (column.sortFn) {
        result = column.sortFn(a, b);
      } else if (column.getSortValue) {
        result = defaultSort(column.getSortValue(a), column.getSortValue(b));
      } else {
        result = defaultSort(column.render(a, 0), column.render(b, 0));
      }
      return sortConfig.direction === "desc" ? -result : result;
    });
  }, [data, sortConfig, columns, enableSorting]);

  const checkIsClickable = useCallback(
    (row: T, rowIndex: number) => {
      if (!onRowClick) return false;
      return isRowClickable ? isRowClickable(row, rowIndex) : true;
    },
    [onRowClick, isRowClickable],
  );

  const isEmpty = data.length === 0;

  return (
    <div
      className={cn(
        "relative rounded-xl border border-surface-700/40 overflow-hidden bg-surface-850",
        containerClassName,
      )}
    >
      <div className={cn("relative overflow-y-auto", scrollMaxHeight)}>
        <table className={cn("w-full", tableClassName)}>
          {!isEmpty && (
            <thead className="sticky top-0 z-10 bg-surface-800">
              <tr className="border-b border-separator">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-3 py-3 text-left text-xs font-bold tracking-widest text-surface-400",
                      col.headerClassName,
                    )}
                    scope="col"
                  >
                    {enableSorting ? (
                      <SortableHeader
                        column={col as TableColumn<unknown>}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      >
                        {col.header}
                      </SortableHeader>
                    ) : (
                      col.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-16">
                  {emptyState}
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <TableRow
                  key={getRowKey ? getRowKey(row, rowIndex) : rowIndex}
                  row={row}
                  rowIndex={rowIndex}
                  columns={columns}
                  rowClassName={rowClassName}
                  globalCellClassName={globalCellClassName}
                  onRowClick={onRowClick}
                  isClickable={checkIsClickable(row, rowIndex)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
